import { useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Copy,
  Download,
  Send,
  MoreHorizontal,
  Pen,
  ReceiptEuro,
  ReceiptText,
  Trash2,
} from 'lucide-react';
import { AlertDialogComponent } from '@/components/alert-dialog';
import {
  deleteInvoice,
  duplicateInvoice,
  changeInvoiceStatus,
} from '@/actions';
import { TInvoiceData } from '@/types/invoice';
import { DownloadInvoice } from '@/features/invoices/download/download-invoice';
import { useInvoiceContext } from '@/contexts/invoice-context';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { LoadingButton } from '@/components/loading-button';
import { SendInvoiceDialog } from '@/features/single-invoice/send-invoice-dialog';

interface Props {
  invoice: TInvoiceData;
  t: (key: string) => string;
}

export const InvoiceActions = ({ invoice, t }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSendOpen, setIsSendOpen] = useState(false);
  const { dispatch } = useInvoiceContext();

  const { status, wrap } = useSubmitStatus();

  const handleDelete = () => {
    wrap(async () => {
      await deleteInvoice(invoice.id);
    }).then(() => {
      setTimeout(() => {
        setIsDialogOpen(false);
      }, 1000);
    });
  };

  return (
    <div className="flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-label="Open menu"
          >
            <span className="sr-only">{t('openMenu')}</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="min-w-36">
          <DropdownMenuLabel>{t('actions')}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <Link href={`/invoices/${invoice.id}`}>
            <DropdownMenuItem className="cursor-pointer">
              <ReceiptText className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">{t('viewInvoice')}</span>
            </DropdownMenuItem>
          </Link>

          <DownloadInvoice invoiceId={invoice.id}>
            <DropdownMenuItem
              className="cursor-pointer"
              onSelect={(e) => {
                e.preventDefault();
              }}
            >
              <Download className="w-4 h-4" color="#787B80" />
              <span className="text-sm font-medium">
                {t('downloadInvoice')}
              </span>
            </DropdownMenuItem>
          </DownloadInvoice>

          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={(e) => {
              e.preventDefault();
              requestAnimationFrame(() => setIsSendOpen(true));
            }}
          >
            <Send className="w-4 h-4" color="#787B80" />
            <span className="text-sm font-medium">{t('sendInvoice')}</span>
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={async () => {
              const result = await duplicateInvoice(invoice.id);
              if (result.error) {
                console.error('Error duplicating invoice:', result.error);
              }
            }}
          >
            <Copy className="w-4 h-4" color="#787B80" />
            <span className="text-sm font-medium">{t('duplicateInvoice')}</span>
          </DropdownMenuItem>

          {invoice.status !== 'PAID' && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={async () => {
                  const result = await changeInvoiceStatus(invoice.id);
                  if (result.error) {
                    console.error(
                      'Error changing invoice status:',
                      result.error
                    );
                  }
                }}
              >
                <ReceiptEuro className="w-4 h-4" color="#787B80" />

                <span className="text-sm font-medium">{t('markAsPaid')}</span>
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              dispatch({
                type: 'OPEN_EDIT_INVOICE_POPUP',
                payload: { invoiceId: invoice.id },
              });
            }}
          >
            <Pen className="w-4 h-4" color="#787B80" />
            <span className="text-sm font-medium">{t('editInvoice')}</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="cursor-pointer"
            onClick={() => {
              requestAnimationFrame(() => setIsDialogOpen(true));
            }}
          >
            <Trash2 className="w-4 h-4" color="#787B80" />
            <span className="text-sm font-medium">{t('deleteInvoice')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SendInvoiceDialog
        open={isSendOpen}
        onOpenChange={setIsSendOpen}
        invoiceId={invoice.id}
        clientEmail={invoice.clientEmail}
      />

      <AlertDialogComponent
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title={t('deleteInvoiceAlert.title')}
        description={t('deleteInvoiceAlert.description')}
        cancelLabel={t('deleteInvoiceAlert.cancelLabel')}
        deleteLabel={
          <LoadingButton
            isFull={false}
            type="button"
            status={status}
            idleText="Delete"
            loadingText="Deleting..."
            successText="Deleted"
            errorText="Failed"
            onClick={handleDelete}
          />
        }
      />
    </div>
  );
};
